import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import SendIcon from "@mui/icons-material/Send";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Container,
  Divider,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import moment from "moment";
import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { ConsignmentRepository } from "../../repositories/ConsignmentRepository";

const Detail = ({ label, value }) =>
  value ? (
    <Box>
      <Typography color="text.secondary" variant="caption">
        {label}
      </Typography>
      <Typography sx={{ fontSize: 16, fontWeight: 600, overflowWrap: "anywhere" }}>
        {value}
      </Typography>
    </Box>
  ) : null;

export default function PublicPresentation() {
  const { token } = useParams();
  const [details, setDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    ConsignmentRepository.getPublicPresentation(token)
      .then(({ data }) => setDetails(data))
      .catch(() => setError("This presentation link is invalid or no longer available."))
      .finally(() => setLoading(false));
  }, [token]);

  const sendPresentation = () => {
    setSending(true);
    setError("");
    ConsignmentRepository.sendPublicPresentation(token)
      .then(() =>
        setDetails((current) => ({ ...current, presentationSent: true })),
      )
      .catch((err) =>
        setError(
          err?.response?.data?.message ||
            "The presentation could not be sent. Please try again or contact Asta Adria.",
        ),
      )
      .finally(() => setSending(false));
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        bgcolor: "#f3f7f8",
        py: { xs: 2, sm: 6 },
        px: 1,
      }}
    >
      <Container maxWidth="sm">
        <Paper elevation={2} sx={{ borderRadius: 3, overflow: "hidden" }}>
          <Box sx={{ bgcolor: "#fff", p: { xs: 2.5, sm: 4 }, textAlign: "center" }}>
            <img src="/logo.png" alt="Asta Adria" style={{ maxWidth: 210, width: "70%" }} />
            <Typography variant="h5" sx={{ mt: 2, fontSize: { xs: 22, sm: 26 } }}>
              ENS presentation
            </Typography>
          </Box>
          <Divider />
          <Box sx={{ p: { xs: 2.5, sm: 4 } }}>
            {loading ? (
              <Box sx={{ py: 8, textAlign: "center" }}>
                <CircularProgress />
              </Box>
            ) : error && !details ? (
              <Alert severity="error">{error}</Alert>
            ) : (
              <Stack spacing={3}>
                {details.presentationSent ? (
                  <Alert icon={<CheckCircleOutlineIcon />} severity="success">
                    Presentation sent successfully.
                  </Alert>
                ) : (
                  <Alert icon={<WarningAmberIcon />} severity="warning" variant="filled">
                    <Typography sx={{ fontWeight: 800 }}>
                      DO NOT SEND IF NO DRIVER IS PRESENT ON SITE
                    </Typography>
                    This action cannot be undone.
                  </Alert>
                )}

                <Box>
                  <Typography sx={{ mb: 2, fontSize: 17 }}>
                    Are you sure you want to send the presentation?
                  </Typography>
                  <Stack spacing={2}>
                    <Detail label="MRN" value={details.mrn} />
                    <Detail
                      label="Customs office"
                      value={`${details.customsOffice || ""}${
                        details.customsOfficeCode ? ` (${details.customsOfficeCode})` : ""
                      }`}
                    />
                    <Detail
                      label="Vehicle"
                      value={[details.truckPlateNumber, details.trailerPlateNumber]
                        .filter(Boolean)
                        .join(" / ")}
                    />
                    <Detail
                      label="Estimated arrival"
                      value={
                        details.arrivalDate
                          ? moment(details.arrivalDate).format("DD-MM-YYYY HH:mm")
                          : ""
                      }
                    />
                  </Stack>
                </Box>

                {error && <Alert severity="error">{error}</Alert>}
                <Button
                  fullWidth
                  size="large"
                  variant="contained"
                  color="secondary"
                  disabled={sending || details.presentationSent}
                  onClick={sendPresentation}
                  startIcon={sending ? <CircularProgress size={20} color="inherit" /> : <SendIcon />}
                  sx={{ minHeight: 52, fontSize: 16, fontWeight: 700 }}
                >
                  {sending
                    ? "Sending…"
                    : details.presentationSent
                      ? "Presentation sent"
                      : "Send presentation"}
                </Button>
              </Stack>
            )}
          </Box>
        </Paper>
      </Container>
    </Box>
  );
}
